import { signal } from '@preact/signals'
import { useLocation } from 'wouter'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { ErrorBanner } from '../../components/ErrorBanner'
import { TextInput } from '../../components/TextInput'
import { useAuth } from '../../hooks/useAuth'
import { AuthLayout } from '../../layouts/AuthLayout'
import { ROUTES } from '../../routes'
import { api } from '../../services/api'
import type { ValidateInviteResponse } from '../../services/types'
import { styled } from '../../setup'
import { borders, colors, spacing, typography } from '../../theme'
import { clearStoredInviteCode, getStoredInviteCode, storeInviteCode } from './LandingPage'

const Form = styled('form')`
  width: 100%;
`

const InviteInfo = styled('div')`
  background: ${colors.successBg};
  border: ${borders.widthMedium} solid ${colors.successBorder};
  border-radius: ${borders.radius};
  padding: ${spacing.md} ${spacing.lg};
  margin-bottom: ${spacing.lg};
  font-size: ${typography.fontSizeBase};
  color: ${colors.success};
  font-weight: ${typography.fontWeightMedium};
`

// Local form state
const inviteCode = signal('')
const displayName = signal('')
const isSubmitting = signal(false)
const validationError = signal<string | null>(null)
const validatedInvite = signal<{ code: string; kind: string } | null>(null)

// Validation
const DISPLAY_NAME_REGEX = /^[a-zA-Z0-9_]+$/
const MIN_DISPLAY_NAME_LENGTH = 2
const MAX_DISPLAY_NAME_LENGTH = 30

function validateDisplayName(): boolean {
  if (displayName.value.length < MIN_DISPLAY_NAME_LENGTH) {
    validationError.value = 'Display name must be at least 2 characters'
    return false
  }

  if (displayName.value.length > MAX_DISPLAY_NAME_LENGTH) {
    validationError.value = 'Display name cannot exceed 30 characters'
    return false
  }

  if (!DISPLAY_NAME_REGEX.test(displayName.value)) {
    validationError.value = 'Display name can only contain letters, numbers, and underscores'
    return false
  }

  validationError.value = null
  return true
}

export function SignupPage() {
  const { signup, error, clearAuthError } = useAuth()
  const [, setLocation] = useLocation()

  // Get stored invite code from session (may be null if lost during redirect)
  const storedInvite = getStoredInviteCode()

  // Use stored invite or validated invite from this session
  const currentInvite = storedInvite || validatedInvite.value

  const handleValidateInvite = async (e: Event) => {
    e.preventDefault()

    const code = inviteCode.value.trim().toUpperCase()
    if (code.length < 6) {
      validationError.value = 'Invite code must be at least 6 characters'
      return
    }

    isSubmitting.value = true
    validationError.value = null

    try {
      const response = await api.get<ValidateInviteResponse>(
        `/auth/validate-invite?code=${encodeURIComponent(code)}`,
      )

      if (response.valid && response.kind) {
        // Store for future and update local state
        storeInviteCode(code, response.kind)
        validatedInvite.value = { code, kind: response.kind }
      } else {
        validationError.value = response.error || 'Invalid invite code'
      }
    } catch (err: any) {
      validationError.value = err.message || 'Failed to validate invite code'
    } finally {
      isSubmitting.value = false
    }
  }

  const handleSubmit = async (e: Event) => {
    e.preventDefault()

    if (!currentInvite) return
    if (!validateDisplayName()) return

    isSubmitting.value = true
    clearAuthError()

    try {
      const profile = await signup(currentInvite.code, displayName.value)

      // Clear stored invite code and form
      clearStoredInviteCode()
      displayName.value = ''
      validatedInvite.value = null

      // Redirect based on profile type
      if (profile.type === 'CHILD' && profile.childLinkState === 'UNLINKED') {
        setLocation(ROUTES.CHILD_LINK_REQUEST)
      } else {
        setLocation(ROUTES.PLAY)
      }
    } catch (_err) {
      // Error is handled by store
    } finally {
      isSubmitting.value = false
    }
  }

  // If no invite code, show invite code form
  if (!currentInvite) {
    return (
      <AuthLayout>
        <Card title="Enter Invite Code" maxWidth="400px">
          <Form onSubmit={handleValidateInvite}>
            {validationError.value && (
              <ErrorBanner
                message={validationError.value}
                onDismiss={() => {
                  validationError.value = null
                }}
              />
            )}

            <TextInput
              label="Invite Code"
              signal={inviteCode}
              placeholder="Enter your invite code"
              autoComplete="off"
              autoFocus
            />

            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting.value}
              disabled={isSubmitting.value}
              fullWidth
            >
              Validate Code
            </Button>
          </Form>
        </Card>
      </AuthLayout>
    )
  }

  // Have invite code, show profile creation form
  return (
    <AuthLayout>
      <Card title="Create Your Profile" maxWidth="400px">
        <Form onSubmit={handleSubmit}>
          <InviteInfo>
            Creating {currentInvite.kind === 'CHILD' ? 'a child' : 'an adult'} account
          </InviteInfo>

          {(error.value || validationError.value) && (
            <ErrorBanner
              message={validationError.value || error.value || ''}
              onDismiss={() => {
                validationError.value = null
                clearAuthError()
              }}
            />
          )}

          <TextInput
            label="Display Name"
            signal={displayName}
            placeholder="Choose a display name"
            hint="2-30 characters, letters, numbers, and underscores only"
            autoComplete="username"
            autoFocus
          />

          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting.value}
            disabled={isSubmitting.value}
            fullWidth
          >
            Create Profile
          </Button>
        </Form>
      </Card>
    </AuthLayout>
  )
}
